import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Platform } from '@app/classes';
import { BulkSelectEmployeeDialogComponent, ConfirmationDialogComponent } from '@app/components';
import { modelsNotInModels } from '@app/functions';
import { Module } from '@app/models/common/module.model';
import { QueryFetcher } from '@app/models/core/query-fetcher.model';
import { Employee } from '@app/models/employee/employee.model';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { EmployeesAssignedToTimeTracking } from '@app/modules/time-tracking/services/tables/employees-assigned-to-time-tracking/employees-assigned-to-time-tracking.tables';
import { AuthService, EmployeeModuleAssignmentService, NotifyService } from '@app/services';
import { EmployeeModuleAssignmentData } from '@app/types/employee-module-assignment.type';
import { Subject } from 'rxjs';
import { TimeTrackingMenuService } from '../../services';
import { TimeTrackingSettingsService } from '../../services/time-tracking-settings.service';

@Component({
    templateUrl: './time-tracking-settings.template.html',
    styleUrls: ['./time-tracking-settings.style.scss'],
})
export class TimeTrackingSettingsView implements OnInit, OnDestroy {
    @ViewChild('selectEmployees', { static: true }) selectEmployees!: BulkSelectEmployeeDialogComponent;
    @ViewChild('confirmRemoveModal') confirmRemoveModal!: ConfirmationDialogComponent;

    /**
     * employeesOnTimeTracking is the employees who are already on time tracking (assigned to the module)
     */
    employeesOnTimeTracking: Employee[] = [];

    /**
     * adminsOnTimeTracking is used to determine if we are removing all admins from time tracking
     */
    adminsOnTimeTracking: Employee[] = [];

    showAtLeastOneAdminModal = false;

    companyId = this.auth.company?.id as number;
    timeTrackingModule = this.auth.company?.modules.find(
        (module) => module.name === Platform.modules.timeTracking
    ) as Module;

    isLoading = true;
    isOnTimeTracking = false;
    isOnPayroll = false;

    /**
     * removeOrConfirmLabel is used by the confirmation dialog to show the correct label
     * depending on whether the user is removing or confirming the removal of employees
     */
    removeOrConfirmLabel: 'time-tracking.remove' | 'time-tracking.confirm' = 'time-tracking.remove';

    /**
     * employeesPendingRemoval is used by the confirmation dialog to show the employees that will be removed
     */
    employeesPendingRemoval: Employee[] = [];

    /**
     * employeesPendingRemovalWithPendingHours is used by the confirmation dialog to show the employees that will be removed
     * and have pending hours
     */
    employeesPendingRemovalWithPendingHours: Employee[] = [];

    /**
     * projectOwnersPendingRemoval is used by the confirmation dialog to show the project owners that will be removed.
     * project owners is a subset of all employees being removed.
     */
    projectOwnersPendingRemoval: Employee[] = [];

    private _onDestroy = new Subject<void>();

    constructor(
        public table: EmployeesAssignedToTimeTracking,
        public menuService: TimeTrackingMenuService,
        private notifyService: NotifyService,
        private employeeModuleAssignmentService: EmployeeModuleAssignmentService,
        private auth: AuthService,
        private timeTrackingSettingsService: TimeTrackingSettingsService
    ) {
        this.isOnTimeTracking = this.auth.employee?.hasModule(Platform.modules.timeTracking) ?? false;
        this.isOnPayroll = Boolean(this.auth.company?.isPayrollSyncEnabled);
    }

    async ngOnInit(): Promise<void> {
        await this.reload();
        this.isLoading = false;
        this.table.onLoading.subscribe((isLoading) => (this.isLoading = isLoading));
    }

    ngOnDestroy(): void {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    async openEmployeeSelect(): Promise<void> {
        const employees = await this.selectEmployees.show<Employee[]>(this.employeesOnTimeTracking);

        // if the user cancels the dialog, employees will be null
        if (employees === null) {
            return;
        }

        this.bulkAssign(employees);
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;

        const employee = data as Employee;

        switch (menuItem.event) {
            case 'remove':
                this.remove(employee);
                break;
        }
    }

    tooltipTextMaker(employee: Employee): string | null {
        if (employee.hasPendingTimeTrackingHours) {
            return 'time-tracking.employeeHasPendingHours';
        }

        return null;
    }

    withTimeTrackingAttributes(query: QueryFetcher): QueryFetcher {
        return query.with('timeTrackingAttributes');
    }

    /**
     * remove removes a single employee from time tracking
     * it first asks for confirmation
     */
    private async remove(employeeToRemove: Employee): Promise<void> {
        this.removeOrConfirmLabel = 'time-tracking.remove';

        const confirmRemoval = await this.confirmRemoval([employeeToRemove]);
        if (!confirmRemoval) {
            return;
        }

        const success = await this.assign([], [employeeToRemove]);
        if (success) {
            this.notifyService.success('time-tracking.employeeRemoved');
        }
    }

    /**
     * bulkAssign adds and removes multiple employees from time tracking
     * it first asks for confirmation for the removal of employees
     */
    private async bulkAssign(employeesWhoWillBeOnTimeTracking: Employee[]): Promise<void> {
        this.removeOrConfirmLabel = 'time-tracking.confirm';
        const employeesToAttach: Employee[] = modelsNotInModels(
            employeesWhoWillBeOnTimeTracking,
            this.employeesOnTimeTracking
        );

        const employeesToDetach: Employee[] = modelsNotInModels(
            this.employeesOnTimeTracking,
            employeesWhoWillBeOnTimeTracking
        );

        const confirmRemoval = await this.confirmRemoval(employeesToDetach);
        if (!confirmRemoval) {
            this.openEmployeeSelect();
            return;
        }

        const success = await this.assign(employeesToAttach, employeesToDetach);
        if (success) {
            this.notifyService.success('time-tracking.assignedEmployeesUpdated');
        }
    }

    /**
     * assign assigns employees to time tracking
     * this is where the actual persistence happens
     *
     * return true if any additions or removals were done
     */
    private async assign(employeesToAttach: Employee[], employeesToDetach: Employee[]): Promise<boolean> {
        const adminsNotBeingRemoved = this.adminsOnTimeTracking.filter(
            (e) => !employeesToDetach.some((detach) => detach.is(e))
        );

        const adminsBeingAdded = employeesToAttach.filter((e) => e.hasTimeTrackingAdminPermission);

        if (adminsNotBeingRemoved.length + adminsBeingAdded.length === 0) {
            this.showAtLeastOneAdminModal = true;
            return false;
        }

        const moduleId = this.timeTrackingModule.id;
        const isAttachingSelf = employeesToAttach.some((e) => e.is(this.auth.employee));
        const isDetachingSelf = employeesToDetach.some((e) => e.is(this.auth.employee));

        const attach: EmployeeModuleAssignmentData[] = employeesToAttach.map((employee: Employee) => ({
            employeeId: employee.id,
            moduleId,
        }));

        const detach: EmployeeModuleAssignmentData[] = employeesToDetach.map((employee: Employee) => ({
            employeeId: employee.id,
            moduleId,
        }));

        if (attach.length + detach.length === 0) {
            return false;
        }

        this.isLoading = true;

        try {
            await this.employeeModuleAssignmentService.assign(attach, detach);

            // if we are attaching or detaching the current employee
            // we want to rehydrate their modules so that
            // menus show the correct options
            if (isAttachingSelf || isDetachingSelf) {
                await this.auth.hydrate();
                this.menuService.updateMenu();
                this.isOnTimeTracking = this.auth.employee?.hasModule(Platform.modules.timeTracking) ?? false;
            }
        } catch (e) {
            this.notifyService.error('time-tracking.assignToTimeTrackingError');
            return false;
        }

        await this.reload();

        this.isLoading = false;
        return true;
    }

    private async reload(): Promise<void> {
        try {
            const [employees] = await Employee.param('company', this.companyId)
                .where('isAssignedModule', Platform.modules.timeTracking)
                .with(['position', 'department', 'timeTrackingAttributes', 'activeTimeTrackingProjectOwnerships'])
                .all();

            this.employeesOnTimeTracking = employees;
            this.adminsOnTimeTracking = employees.filter((e) => e.hasTimeTrackingAdminPermission);
            this.table.reload();
            this.timeTrackingSettingsService.invalidate();
        } catch (e) {
            this.notifyService.error('time-tracking.generalError');
        }
    }

    /**
     * confirmRemoval shows a confirmation dialog for the removal of employees
     * it also sets so instance variables needed for the ui
     */
    private async confirmRemoval(employeesToRemove: Employee[]): Promise<boolean> {
        if (!employeesToRemove.length) {
            return true;
        }

        this.employeesPendingRemoval = employeesToRemove;
        this.employeesPendingRemovalWithPendingHours = employeesToRemove.filter((e) => e.hasPendingTimeTrackingHours);
        this.projectOwnersPendingRemoval = employeesToRemove.filter(
            (e) => e.activeTimeTrackingProjectOwnerships.length !== 0
        );
        const shouldRemove = await this.confirmRemoveModal.open();

        return shouldRemove;
    }
}
